/**
 * API Service
 * Core API client with axios configuration and interceptors
 * Migrated from helpers/api_helper.ts
 */

import axios from "axios";
import secureStorage from '../utils/secureStorage';

// Set base URL from environment variable
axios.defaults.baseURL = process.env.REACT_APP_API_URL || "";

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// Get token from secure storage
const getAccessToken = () => {
  return secureStorage.getAccessToken();
};

// Note: We don't set axios.defaults.headers.common["Authorization"] here anymore
// because it would apply to ALL requests including public login endpoints.
// Instead, we use the request interceptor below to add Authorization header
// only to authenticated endpoints.

// Public endpoints that should NOT have Authorization header
const PUBLIC_ENDPOINTS = [
  '/auth/web/corporate-login',
  '/auth/web/field-user-login',
  '/auth/web/send-field-user-otp',
  '/auth/web/lookup-user',
  '/auth/web/organization-branding',
  '/auth/web/verify-otp',
  '/auth/web/resend-otp',
  '/auth/web/resend-otp-generic',
  '/auth/signup',
  '/auth/signin',
  '/auth/forgot-password',
  '/health',
  '/public/',
];

// Auth endpoints where 401 means "invalid credentials" not "session expired"
const AUTH_ENDPOINTS = [
  '/auth/web/corporate-login',
  '/auth/web/field-user-login',
  '/auth/web/send-field-user-otp',
  '/auth/web/lookup-user',
  '/auth/web/verify-otp',
  '/auth/web/resend-otp',
  '/auth/web/resend-otp-generic',
  '/auth/signin',
  '/auth/signup',
  '/auth/forgot-password',
];

/**
 * Check if a URL is an authentication endpoint
 */
const _isAuthEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Request interceptor - Add auth token to authenticated requests only
axios.interceptors.request.use(
  (config) => {
    // 1. Skip if Authorization header is already set (e.g. by AITextArea for Groq/OpenAI)
    if (config.headers["Authorization"]) {
      return config;
    }

    // 2. Skip for external AI/LLM providers (checking absolute URLs)
    if (config.url?.startsWith('http') && (
        config.url.includes('api.groq.com') || 
        config.url.includes('api.openai.com') || 
        config.url.includes('googleapis.com') ||
        config.url.includes('api-inference.huggingface.co') || // Hugging Face
        config.url.includes('localhost:11434') // Ollama
    )) {
      return config;
    }

    // Check if this is a public endpoint
    const isPublicEndpoint = PUBLIC_ENDPOINTS.some(endpoint =>
      config.url?.includes(endpoint)
    );

    // Only add Authorization header if NOT a public endpoint
    if (!isPublicEndpoint) {
      const token = getAccessToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  async function (error) {
    // Handle other errors
    let message;
    switch (error.response?.status) {
      case 500:
        message = "Internal Server Error";
        break;
      case 401:
        message = "Invalid credentials or session expired";
        break;
      case 403:
        message = "Access denied";
        break;
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      case 429:
        message = "Too many requests. Please try again later.";
        break;
      default:
        message = error.response?.data?.message || error.message || "An error occurred";
    }
    return Promise.reject({
      message,
      status: error.response?.status,
      response: error.response
    });
  }
);

/**
 * Sets the access token in secure storage
 * Note: Authorization header is added by request interceptor, not set globally
 * @param {*} token
 */
const setAuthorization = (token: any) => {
  // Store token in secure storage - interceptor will use it
  secureStorage.setAccessToken(token);
};

class APIClient {
  /**
   * Fetches data from given url
   */
  get = (url: any, params: any) => {
    let response;

    let paramKeys: any = [];

    if (params && Object.keys(params).length > 0) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys.join('&');
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  /**
   * post given data to url
   */
  create = (url: any, data: any) => {
    return axios.post(url, data);
  };

  /**
   * Updates data
   */
  update = (url: any, data: any) => {
    return axios.patch(url, data);
  };

  put = (url: any, data: any) => {
    return axios.put(url, data);
  };

  /**
   * Delete
   */
  delete = (url: any, config: any) => {
    return axios.delete(url, { ...config });
  };

  /**
   * Simple post function - similar to apiService.post
   * @param endpoint - API endpoint (e.g., '/Master/productList')
   * @param data - Request data object
   * @returns Promise with response data
   */
  post = async (endpoint: string, data: any = {}) => {
    return axios.post(endpoint, data);
  };

  /**
   * File upload function - handles FormData with proper headers
   * Uses POST method for creating new uploads
   * @param endpoint - API endpoint
   * @param formData - FormData object containing file and other data
   * @param onProgress - Optional progress callback
   * @returns Promise with response data
   */
  uploadFile = async (endpoint: string, formData: FormData, onProgress?: (progressEvent: any) => void) => {
    // Note: Authorization header is added by request interceptor automatically
    // For FormData, we must NOT set Content-Type - browser sets it with boundary
    const response = await axios.post(endpoint, formData, {
      headers: {
        'Content-Type': undefined, // Let browser set multipart/form-data with boundary
      },
      onUploadProgress: onProgress,
    });
    return response.data;
  };

  /**
   * File upload function with PUT method - for updating existing resources with files
   * @param endpoint - API endpoint
   * @param formData - FormData object containing file and other data
   * @param onProgress - Optional progress callback
   * @returns Promise with response data
   */
  uploadFilePut = async (endpoint: string, formData: FormData, onProgress?: (progressEvent: any) => void) => {
    // Note: Authorization header is added by request interceptor automatically
    // For FormData, we must NOT set Content-Type - browser sets it with boundary
    const response = await axios.put(endpoint, formData, {
      headers: {
        'Content-Type': undefined, // Let browser set multipart/form-data with boundary
      },
      onUploadProgress: onProgress,
    });
    return response.data;
  };

  /**
   * @deprecated Use uploadFile instead. This method has incorrect header handling.
   * Kept for backward compatibility but should be migrated.
   */
  FileData = async (endpoint: string, formData: FormData) => {
    // Redirect to proper uploadFile method
    return this.uploadFile(endpoint, formData);
  };
}

const getLoggedUser = () => {
  return secureStorage.getAuthUser();
};

/**
 * Standalone get function (can be used without APIClient instance)
 * @param endpoint - API endpoint
 * @param params - Query parameters
 * Note: Response interceptor already extracts response.data, so we return the unwrapped data
 */
export const get = async (endpoint: string, params: any = {}): Promise<any> => {
  const response = await axios.get(endpoint, { params });
  return response; // Response interceptor already extracts .data
};

/**
 * Standalone post function (can be used without APIClient instance)
 * @param endpoint - API endpoint
 * @param data - Request data
 * Note: Response interceptor already extracts response.data, so we return the unwrapped data
 */
export const post = async (endpoint: string, data: any = {}): Promise<any> => {
  const response = await axios.post(endpoint, data);
  return response; // Response interceptor already extracts .data
};

/**
 * Standalone put function (can be used without APIClient instance)
 * @param endpoint - API endpoint
 * @param data - Request data
 * Note: Response interceptor already extracts response.data, so we return the unwrapped data
 */
export const put = async (endpoint: string, data: any = {}): Promise<any> => {
  const response = await axios.put(endpoint, data);
  return response; // Response interceptor already extracts .data
};

/**
 * Standalone delete function (can be used without APIClient instance)
 * @param endpoint - API endpoint
 * @param config - Axios config
 * Note: Response interceptor already extracts response.data, so we return the unwrapped data
 */
export const del = async (endpoint: string, config: any = {}): Promise<any> => {
  const response = await axios.delete(endpoint, config);
  return response; // Response interceptor already extracts .data
};

/**
 * Standalone file upload function for POST requests
 * @param endpoint - API endpoint
 * @param formData - FormData object
 * @param onProgress - Optional progress callback
 */
export const uploadFile = async (endpoint: string, formData: FormData, onProgress?: (progressEvent: any) => void) => {
  // Authorization header is added by request interceptor
  // Content-Type must NOT be set for FormData - browser sets it with boundary
  const response = await axios.post(endpoint, formData, {
    headers: {
      'Content-Type': undefined,
    },
    onUploadProgress: onProgress,
  });
  return response.data;
};

/**
 * Standalone file upload function for PUT requests
 * @param endpoint - API endpoint
 * @param formData - FormData object
 * @param onProgress - Optional progress callback
 */
export const uploadFilePut = async (endpoint: string, formData: FormData, onProgress?: (progressEvent: any) => void) => {
  // Authorization header is added by request interceptor
  // Content-Type must NOT be set for FormData - browser sets it with boundary
  const response = await axios.put(endpoint, formData, {
    headers: {
      'Content-Type': undefined,
    },
    onUploadProgress: onProgress,
  });
  return response.data;
};

/**
 * @deprecated Use uploadFile instead. Kept for backward compatibility.
 */
export const FileData = async (endpoint: string, formData: FormData) => {
  return uploadFile(endpoint, formData);
};

// Create singleton instance for default export
const apiService = new APIClient();

export { APIClient, setAuthorization, getLoggedUser };
export default apiService;
